import { Button, Form, Input, Modal } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { CompetencesFormProps } from "./LearningCompetenceForm.types";
import i18n from "../../../i18n";
import { NOTIFICATION_TYPES, openNotification } from "../../Notifications/NotificationsUtils";

const { List, Item, useFormInstance } = Form;
const { TextArea } = Input;

export const getAutoIdentifier = (prefix: string | undefined, index: number, existingIdentifier?: string) => {
  if (existingIdentifier) {
    return existingIdentifier;
  }

  if (!prefix) {
    return "";
  }

  return `${prefix}${String(index + 1).padStart(2, "0")}`;
};

const ProgramCompetenceForm = (props: CompetencesFormProps) => {
  const { t } = i18n;
  const form = useFormInstance(); // <--- ca să putem citi valorile curente

  // Clientul generat aruncă obiectul Response brut (vezi Api/runtime.ts), deci mesajul
  // de eroare se citește din corpul răspunsului, nu dintr-un wrapper.
  const readServerMessage = async (err: any) => {
    if (typeof err?.clone !== "function") {
      return "";
    }

    try {
      return (await err.clone().text()) || "";
    } catch {
      return "";
    }
  };

  const notifyRemoveError = (serverMessage: string) => {
    openNotification(
      "Eroare!",
      serverMessage || "Nu s-a putut efectua stergerea, exista corelari pentru acest item.",
      NOTIFICATION_TYPES.ERROR
    );
  };

  const handleRemove = async (fieldIndex: number, remove: (index: number) => void) => {
    const listValues = form.getFieldValue(props.name as any) || [];
    const currentItem = listValues[fieldIndex];

    if (!currentItem?.id) {
      remove(fieldIndex);
      return;
    }

    const removeFromList = () => {
      remove(fieldIndex);
      props.onSelectionInvalidated?.(currentItem.id);
    };

    if (!props.onRemoveSavedItem) {
      removeFromList();
      return;
    }

    try {
      await props.onRemoveSavedItem(currentItem.id);
      removeFromList();
    } catch (err: any) {
      const serverMessage = await readServerMessage(err);

      // Doar adminii pot forța ștergerea, și doar când serverul a refuzat-o din cauza corelărilor.
      if (!props.allowForceDelete || err?.status !== 409) {
        notifyRemoveError(serverMessage);
        return;
      }

      Modal.confirm({
        title: "Ștergere rezultat al învățării",
        content: "Rezultatul pe care încercați să îl ștergeți are corelări făcute cu discipline sau competențe. Continuați?",
        okText: "Continuă",
        cancelText: "Anulare",
        onOk: async () => {
          try {
            await props.onRemoveSavedItem!(currentItem.id, true);
            removeFromList();
          } catch (forceErr: any) {
            notifyRemoveError(await readServerMessage(forceErr));
          }
        }
      });
    }
  };

  return (
    <List initialValue={props.initialValue} name={props.name} rules={props.rules}>
      {(fields, { add, remove }) => (
        <div className="flex flex-col pb-5">
          {fields.length > 0 && (
            <div style={{ display: "flex", gap: "1rem", paddingBottom: "1rem" }}>
              <label style={{ width: "20%", textAlign: "left" }}>
                <span>*&nbsp;</span>Identificator
              </label>
              <label style={{ width: "80%", textAlign: "left" }}>
                <span style={{ color: "#ff4d4f" }}>*&nbsp;</span>Denumire
              </label>
            </div>
          )}

          {fields.map(({ key, name, ...restField }) => (
            (() => {
              const listValues = form.getFieldValue(props.name as any) || [];
              const currentItem = listValues[name] || {};
              const isReadonlyItem =
                !!currentItem?.isDomainLearningOutcomeTemplate
                || !!currentItem?.isCompetenceTemplate
                || !!currentItem?.isCompetenceTemplateAssociatedOutcome
                || !!currentItem?.isLocked;

              return <div
              key={key}
              className="flex items-start gap-2 border-b border-black mb-3"
              style={{
                display: "flex",
                gap: "1rem",
                paddingBottom: "1rem",
                justifyContent: "space-between",
              }}
            >
              <Item
                {...restField}
                name={[name, "id"]}
                style={{ display: "none" }}
              >
                <Input />
              </Item>

              <Item
                {...restField}
                name={[name, "isDomainLearningOutcomeTemplate"]}
                style={{ display: "none" }}
              >
                <Input />
              </Item>

              <Item
                {...restField}
                name={[name, "isCompetenceTemplate"]}
                style={{ display: "none" }}
              >
                <Input />
              </Item>

              <Item
                {...restField}
                name={[name, "isCompetenceTemplateAssociatedOutcome"]}
                style={{ display: "none" }}
              >
                <Input />
              </Item>

              <Item
                {...restField}
                name={[name, "competenceTemplateId"]}
                style={{ display: "none" }}
              >
                <Input />
              </Item>

              <Item
                {...restField}
                name={[name, "associatedResultTemplateId"]}
                style={{ display: "none" }}
              >
                <Input />
              </Item>

              <Item
                {...restField}
                name={[name, "isLocked"]}
                style={{ display: "none" }}
              >
                <Input />
              </Item>

              <Item
                {...restField}
                name={[name, "identificator"]}
                style={{ display: "none" }}
              >
                <Input />
              </Item>

              <Item
                style={{ width: "20%", marginBottom: "5px" }}
              >
                <Input
                  disabled={true}
                  type="text"
                  placeholder="Identificator"
                  value={getAutoIdentifier(props.identifierPrefix, name, currentItem?.identificator)}
                />
              </Item>

              <Item
                {...restField}
                name={[name, "description"]}
                rules={[{ required: true, message: "Câmp obligatoriu" }]}
                style={{ width: "80%", marginBottom: "5px" }}
              >
                <TextArea
                  disabled={(props.disabled ?? false) || isReadonlyItem}
                  rows={1}
                  placeholder="Introduceți denumirea"
                />
              </Item>

              {(props.allowDeleteLastItem || fields.length > 1) && !props.disabled && !isReadonlyItem && (
                <MinusCircleOutlined
                  className="mt-2"
                  onClick={() => handleRemove(name, remove)}
                />
              )}
            </div>
            })()
          ))}

          {!props.disabled && (
            <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
              {props.buttonText}
            </Button>
          )}
        </div>
      )}
    </List>
  );
};

export default ProgramCompetenceForm;